import AdminUser from './admin/AdminUser';
import ContentManagementInterface from './ContentManagementInterface';

/**
 * Represents a content change proposal in a review
 */
export interface ContentChangeProposal {
  sectionId: string; // Unique identifier for the content section
  sectionType: string; // Type of content (StandardHeaderText, ImageCarousel, etc.)
  contentBefore: any; // Original content from published version
  contentAfter: any; // Proposed new content
  commentId?: string; // Associated comment external ID
  changed: boolean; // Whether this section has been modified
  accepted?: boolean; // Whether the change has been accepted
  acceptedBy?: { id: string; name: string }; // User who accepted the change
  acceptedAt?: string; // When the change was accepted
  createdAt?: string;
  updatedAt?: string;
}

export interface ReviewComment {
  message: string; // The comment text
  submittedBy: { id: string; name: string }; // User who submitted the comment
  createdAt: string;
}

/**
 * Comment attached to a section in the review
 */
export interface ReviewSectionConversation {
  conversationId: string; // External ID of the comment
  sectionId: string; // Which section this comment is attached to
  comments: ReviewComment[];
  isAccepted?: boolean; // Whether the comment has been accepted
  isWithdrawn?: boolean; // Whether the comment has been withdrawn
  withdrawnBy?: string; // User who withdrew the comment
  withdrawnAt?: string; // When the comment was withdrawn
}

/**
 * Review data stored in GCS at /contentReview/[reviewId]/data.json
 * Contains all proposed changes and comments for a review
 */
export interface ContentReviewInterface {
  reviewExternalId: string;
  fundOpportunityExternalId: string;

  // Base content from published version (read-only reference)
  publishedContent: ContentManagementInterface;

  // Base content from draft version (what is being reviewed)
  draftContent: ContentManagementInterface;

  // Proposed changes for each section
  changes: ContentChangeProposal[];

  // All comments attached to sections
  comments: ReviewSectionConversation[];

  // Metadata
  createdAt: string;
  updatedAt: string;
  version: number; // Increment on each save
}

/**
 * Request payload for updating review data
 */
export interface UpdateReviewDataRequest {
  changes: ContentChangeProposal[];
  comments: ReviewSectionConversation[];
}

/**
 * Request payload for accepting changes in a section
 */
export interface AcceptChangesRequest {
  sectionId: string;
  commentId?: string; // Optional comment to attach to the acceptance
}

/**
 * Request payload for responding to a comment
 */
export interface RespondToCommentRequest {
  parentCommentId: string;
  response: string;
  submittedBy: string;
}

/**
 * Request payload for withdrawing a change suggestion
 */
export interface WithdrawChangeRequest {
  sectionId: string;
  commentId: string;
  reason?: string;
}

/**
 * Request payload for submitting for review again
 */
export interface ResubmitForReviewRequest {
  reviewExternalId: string;
  changes: ContentChangeProposal[];
  comments: ReviewSectionConversation[];
}

/**
 * Response from fetching review data
 */
export interface ReviewDataResponse {
  data: ContentReviewInterface;
}

/**
 * Helper to create empty review interface
 */
export const createEmptyReviewInterface = (
  reviewExternalId: string,
  fundOpportunityExternalId: string,
  publishedContent: ContentManagementInterface,
  draftContent: ContentManagementInterface,
): ContentReviewInterface => {
  return {
    reviewExternalId,
    fundOpportunityExternalId,
    publishedContent,
    draftContent,
    changes: [],
    comments: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: 1,
  };
};

/**
 * Helper to get section change by ID
 */
export const getSectionChange = (
  reviewData: ContentReviewInterface,
  sectionId: string,
): ContentChangeProposal | undefined => {
  return reviewData.changes.find((change) => change.sectionId === sectionId);
};

/**
 * Helper to update or add a section change
 */
export const updateSectionChange = (
  reviewData: ContentReviewInterface,
  change: ContentChangeProposal,
): ContentReviewInterface => {
  const existingIndex = reviewData.changes.findIndex((c) => c.sectionId === change.sectionId);

  const updatedChanges = [...reviewData.changes];
  if (existingIndex >= 0) {
    updatedChanges[existingIndex] = {
      ...change,
      updatedAt: new Date().toISOString(),
    };
  } else {
    updatedChanges.push({
      ...change,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }

  return {
    ...reviewData,
    changes: updatedChanges,
    updatedAt: new Date().toISOString(),
    version: reviewData.version + 1,
  };
};

/**
 * Helper to add a comment to a section
 */
export const addSectionComment = (
  reviewData: ContentReviewInterface,
  comment: ReviewSectionConversation,
): ContentReviewInterface => {
  return {
    ...reviewData,
    comments: [...reviewData.comments, comment],
    updatedAt: new Date().toISOString(),
    version: reviewData.version + 1,
  };
};

/**
 * Helper to accept changes for a section
 */
export const acceptSectionChanges = (
  reviewData: ContentReviewInterface,
  sectionId: string,
  acceptedBy: AdminUser,
  acceptanceComment?: string,
): ContentReviewInterface => {
  // Mark the change as accepted
  const updatedChanges = reviewData.changes.map((change) =>
    change.sectionId === sectionId
      ? ({
          ...change,
          accepted: true,
          acceptedBy: { id: acceptedBy.id, name: acceptedBy.name },
          acceptedAt: new Date().toISOString(),
        } as ContentChangeProposal)
      : change,
  );

  // Add acceptance comment as a reply to existing conversation if provided
  const newComments = acceptanceComment
    ? reviewData.comments.map((comment) =>
        comment.sectionId === sectionId
          ? {
              ...comment,
              comments: [
                ...comment.comments,
                {
                  message: acceptanceComment,
                  submittedBy: { id: acceptedBy.id, name: acceptedBy.name },
                  createdAt: new Date().toISOString(),
                } as ReviewComment,
              ],
              isAccepted: true,
            }
          : comment,
      )
    : reviewData.comments;

  return {
    ...reviewData,
    changes: updatedChanges,
    comments: newComments,
    updatedAt: new Date().toISOString(),
    version: reviewData.version + 1,
  };
};

/**
 * Helper to respond to a comment
 */
export const respondToComment = (
  reviewData: ContentReviewInterface,
  parentCommentId: string,
  response: string,
  submittedBy: AdminUser,
): ContentReviewInterface => {
  return {
    ...reviewData,
    comments: reviewData.comments.map((comment) =>
      comment.conversationId === parentCommentId
        ? {
            ...comment,
            comments: [
              ...comment.comments,
              {
                message: response,
                submittedBy: { id: submittedBy.id, name: submittedBy.name },
                createdAt: new Date().toISOString(),
              } as ReviewComment,
            ],
          }
        : comment,
    ),
    updatedAt: new Date().toISOString(),
    version: reviewData.version + 1,
  };
};

/**
 * Helper to withdraw a change suggestion
 */
export const withdrawChangeSuggestion = (
  reviewData: ContentReviewInterface,
  sectionId: string,
  commentId: string,
  withdrawnBy: AdminUser,
  reason?: string,
): ContentReviewInterface => {
  // Find and update the conversation with withdrawal status and add withdrawal response
  const updatedComments = reviewData.comments.map((comment) =>
    comment.conversationId === commentId
      ? {
          ...comment,
          isWithdrawn: true,
          withdrawnBy: withdrawnBy.id,
          withdrawnAt: new Date().toISOString(),
          comments: reason
            ? [
                ...comment.comments,
                {
                  message: `Withdrawn: ${reason}`,
                  submittedBy: { id: withdrawnBy.id, name: withdrawnBy.name },
                  createdAt: new Date().toISOString(),
                } as ReviewComment,
              ]
            : comment.comments,
        }
      : comment,
  );

  return {
    ...reviewData,
    comments: updatedComments,
    updatedAt: new Date().toISOString(),
    version: reviewData.version + 1,
  };
};

/**
 * Helper to check if reviewer can approve (no unaccepted changes or withdrawn suggestions)
 */
export const canReviewerApprove = (reviewData: ContentReviewInterface): boolean => {
  // Check if there are any unaccepted changes
  const hasUnacceptedChanges = reviewData.changes.some(
    (change) => change.changed && !change.accepted,
  );

  // Check if there are any withdrawn suggestions
  const hasWithdrawnSuggestions = reviewData.comments.some((comment) => comment.isWithdrawn);

  return !hasUnacceptedChanges && !hasWithdrawnSuggestions;
};
